import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { listAnnouncements, sendAnnouncement } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Megaphone, Send } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export default function MemberAppAnnouncements() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await listAnnouncements());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load announcements");
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const reach = data?.audiences?.find((a) => a.value === audience)?.count ?? 0;

  const send = async () => {
    if (!window.confirm(`Send "${title.trim()}" to ${reach} member${reach === 1 ? "" : "s"} at ${activeFacility?.name || "this gym"}? This can't be unsent.`)) return;
    setSending(true);
    try {
      const r = await sendAnnouncement({ title: title.trim(), body: body.trim(), audience });
      toast.success(`Sent to ${r.recipients} member${r.recipients === 1 ? "" : "s"}`);
      setTitle("");
      setBody("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div>
        <div className="eyebrow">Member App</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Announcements</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Holidays, events, timing changes: anything every member at {activeFacility?.name || "this gym"} should know.
          It lands in the app&apos;s notifications list and as a phone notification for members who allow gym news.
        </p>
      </div>

      {data && !data.push_enabled && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Phone notifications aren&apos;t switched on yet, so announcements only appear in the app&apos;s notifications list for now.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="card-surface space-y-4 p-5">
          <div className="text-sm font-semibold text-zinc-100">New announcement</div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-400" htmlFor="ann-title">Title</label>
            <Input id="ann-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80}
              placeholder="e.g. Diwali timings" data-testid="ann-title" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-400" htmlFor="ann-body">Message</label>
            <textarea id="ann-body" value={body} onChange={(e) => setBody(e.target.value)} maxLength={500} rows={5}
              placeholder="e.g. We're closed on 1 Nov. On 2 Nov we're open 7-11 am only."
              className="w-full resize-none rounded-md border border-[#262626] bg-[#0b0b0b] px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
              data-testid="ann-body" />
            <div className="text-right text-[10px] text-zinc-500">{body.length}/500</div>
          </div>
          <div className="space-y-2">
            <div className="text-xs text-zinc-400">Send to</div>
            <div className="flex flex-wrap gap-2">
              {(data?.audiences || []).map((a) => (
                <button key={a.value} type="button" onClick={() => setAudience(a.value)} data-testid={`ann-audience-${a.value}`}
                  className={`rounded-full border px-3 py-1 text-xs ${audience === a.value ? "border-[#E5262B] bg-[#E5262B]/15 text-zinc-100" : "border-[#262626] text-zinc-400"}`}>
                  {a.label} · {a.count}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-zinc-500">Counts are members who have signed in to the app.</div>
          </div>
          <div className="flex justify-end">
            <Button onClick={send} disabled={sending || !title.trim() || !body.trim() || !reach} data-testid="ann-send">
              <Send className="mr-1.5 h-4 w-4" /> {sending ? "Sending…" : `Send to ${reach}`}
            </Button>
          </div>
        </div>

        <div className="card-surface p-5">
          <div className="mb-3 text-sm font-semibold text-zinc-100">Sent</div>
          {!data ? <div className="text-sm text-zinc-500">Loading…</div> : data.announcements.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-500">
              <Megaphone className="mx-auto mb-2 h-5 w-5 text-zinc-600" /> Nothing sent yet.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="ann-history">
              {data.announcements.map((a) => (
                <li key={a.id} className="rounded-lg border border-[#1f1f1f] p-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium text-zinc-100">{a.title}</span>
                    <span className="shrink-0 text-[11px] text-zinc-500">{fmt(a.created_at)}</span>
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-zinc-400">{a.body}</div>
                  <div className="mt-2 text-[11px] text-zinc-500">
                    {data.audiences.find((x) => x.value === a.audience)?.label || a.audience} · {a.recipients} member{a.recipients === 1 ? "" : "s"} · by {a.sent_by_name || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
